import { useEffect, useMemo } from 'react'

import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAngleRight, faCheckCircle as faCheckCircleLight, faCircleCheck, faDownload,
} from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle as faCheckCircleSolid } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, Button, Checkbox, DashboardModule, Link, StatusBadge, Table, Text,
} from '@domain-group/suit-theme'

import { capitalizeString } from '@domain-group/javascript-utils'

import useOrderReconciliation, { OrderReconciliationPayload } from '@hooks/useOrderReconciliation'
import useReporting from '@hooks/useReporting'

import BalanceCalculations from '@components/BalanceCalculations'
import NoticeBox from '@components/NoticeBox'

import handleCallbackAction from '@functions/handleCallbackAction'
import { statusColors } from '@functions/status'

import type { CampaignModel, ReconciliationItemModel } from '@models/types'

import EditableTableItem from '../EditableItem'
import type { DefaultState } from '../PageContent'

type BuildColumnsParams = {
  editMode: boolean,
  localCallbacks: {
    setState: (state: Partial<DefaultState>) => void,
  },
  orderReconciliationCallbacks: OrderReconciliationPayload['callbacks'],
  providerSelected?: boolean,
  selectedEntityIds: number[],
  selectedEntityType?: string,
}

const buildColumns = (
  params: BuildColumnsParams,
) => {
  const {
    editMode,
    localCallbacks,
    orderReconciliationCallbacks,
    providerSelected,
    selectedEntityIds,
    selectedEntityType,
  } = params
  const { editReconciliationItem, toggleId } = orderReconciliationCallbacks
  const { setState } = localCallbacks

  const columns = [
    {
      title: '',
      dataKey: 'id',
      cellProps: {
        verticalAlign: 'middle',
        position: 'sticky',
        left: 0,
      },
      hidden: !!editMode,
      render: (id: number, item: ReconciliationItemModel) => {
        if (!editMode && !item.invoice_reference && item.itemType !== 'processing-fees'
          && (selectedEntityType === '' || selectedEntityType === item.itemType)){
          return (
            <Checkbox
              boxProps={{ margin: 0 }}
              checked={selectedEntityIds.includes(id)}
              color={selectedEntityIds.includes(id) ? 'green' : 'grey'}
              onClick={() => {
                if (!selectedEntityType) setState({ selectedEntityType: item.itemType })
                toggleId(id)
              }}
              fontSize="medium"
              iconChecked={selectedEntityIds.includes(id) ? faCheckCircleSolid : faCheckCircleLight}
            />
          )
        }
        return null
      },
    },
    {
      title: 'Title',
      dataKey: 'title',
      cellProps: {
        left: editMode ? 0 : 32,
        position: 'sticky',
        style: { whiteSpace: 'nowrap' },
        verticalAlign: 'middle',
      },
    },
    {
      title: 'Campaign',
      dataKey: 'campaignTitle',
      cellProps: {
        style: { whiteSpace: 'nowrap' },
        verticalAlign: 'middle',
      },
      hidden: !providerSelected,
    },
    {
      title: 'Provider',
      dataKey: 'provider',
      cellProps: {
        verticalAlign: 'middle',
        style: { whiteSpace: 'nowrap' },
      },
    },
    {
      title: 'Order #',
      dataKey: 'orderNumber',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (orderNumber: string) => (
        <Text variant="ellipsis" width={120}>
          {orderNumber}
        </Text>
      ),
    },
    {
      title: 'PO #',
      dataKey: 'purchaseOrder',
      cellProps: {
        verticalAlign: 'middle',
      },
    },
    {
      title: 'Items',
      dataKey: 'itemCount',
      cellProps: {
        verticalAlign: 'middle',
      },
    },
    {
      title: 'Date',
      dataKey: 'date',
      cellProps: {
        verticalAlign: 'middle',
      },
    },
    {
      title: 'Status',
      dataKey: 'status',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      render: (status: string) => (
        <StatusBadge
          boxProps={{ marginLeft: 'medium' }}
          // @ts-expect-error TS7053
          color={statusColors[status.toLowerCase()]}
          ghost
          text={status}
        />
      ),
    },
    {
      title: 'Total',
      dataKey: 'expectedTotal',
      cellProps: {
        textAlign: 'right',
        verticalAlign: 'middle',
      },
      render: (expectedTotal: string, item: ReconciliationItemModel) => (
        <Text color={item.balanced ? 'green' : 'red'}>
          {formatMoney(expectedTotal)}
        </Text>
      ),
    },
    {
      title: 'Invoice #',
      dataKey: 'invoiceNumber',
      cellProps: {
        textAlign: 'right',
        verticalAlign: 'middle',
      },
      render: (invoiceNumber: string, item: ReconciliationItemModel) => {
        if (editMode){
          return (
            <EditableTableItem
              callbacks={orderReconciliationCallbacks}
              item={item}
              dataKey="invoice_reference"
            />
          )
        }

        return <Text color="bodyFontLightColor">{invoiceNumber}</Text>
      },
    },
    {
      title: 'Comments',
      dataKey: 'comments',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (comments: string, item: ReconciliationItemModel) => {
        if (editMode){
          return (
            <EditableTableItem
              callbacks={orderReconciliationCallbacks}
              item={item}
              dataKey="invoice_notes"
            />
          )
        }

        return <Text color="bodyFontLightColor" variant="ellipsis" width={150}>{comments}</Text>
      },
    },
    {
      title: 'Paid By',
      dataKey: 'paid_by',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (paid_by: string, item: ReconciliationItemModel) => {
        if (editMode){
          return (
            <EditableTableItem
              callbacks={orderReconciliationCallbacks}
              item={item}
              dataKey="paid_by"
            />
          )
        }

        return <Text color="bodyFontLightColor">{capitalizeString(paid_by)}</Text>
      },
    },
    {
      title: 'Invoiced Total',
      dataKey: 'actualTotal',
      cellProps: {
        textAlign: 'right',
        verticalAlign: 'middle',
      },
      render: (actualTotal: string, item: ReconciliationItemModel) => {
        if (editMode){
          return (
            <EditableTableItem
              callbacks={orderReconciliationCallbacks}
              item={item}
              dataKey="invoice_total"
            />
          )
        }

        return <Text color="bodyFontLightColor">{formatMoney(actualTotal)}</Text>
      },
    },
    {
      dataKey: '',
      cellProps: {
        verticalAlign: 'middle',
      },
      hidden: editMode,
      render: (id: number, item: ReconciliationItemModel) => {
        if (!editMode && item.itemType !== 'processing-fees'){
          return (
            // @ts-expect-error TS2345
            <Link onClick={() => editReconciliationItem(item)} greyLink>
              <FontAwesomeIcon icon={faAngleRight} />
            </Link>
          )
        }
      },
    },
  ]

  return columns
}

type OrderReconciliationTablePops = {
  agencyId?: number,
  callbacks: {
    setState: (arg: object) => void,
  },
  campaign: CampaignModel,
  dates?: {
    startDate: string,
    endDate: string,
  },
  editMode: boolean,
  providerId?: number,
  selectedEntityType?: string,
  orderReconciliationPayload: ReturnType<typeof useOrderReconciliation>,
  selectedProviderId: number,
}

const OrderReconciliationTable = (props: OrderReconciliationTablePops) => {
  const {
    agencyId,
    callbacks: localCallbacks,
    campaign,
    dates,
    editMode,
    orderReconciliationPayload,
    providerId,
    selectedEntityType,
    selectedProviderId,
  } = props

  const { endDate, startDate } = dates || {}

  const { setState } = localCallbacks

  const providerSelected = !!selectedProviderId

  const {
    callbacks: orderReconciliationCallbacks,
    callbacks: {
      bulkReconcile, deselectAll,
    },
    hasReconciliationItems,
    hasSelectedEntities,
    orderPayload,
    orderReconciliationItems,
    selectedEntityIds,
  } = orderReconciliationPayload
  const { items } = orderReconciliationItems

  useEffect(() => {
    if (!hasSelectedEntities) setState({ selectedEntityType: '' })
  }, [selectedEntityIds.length])

  const memoColumns = useMemo(() => buildColumns({
    editMode,
    localCallbacks,
    orderReconciliationCallbacks,
    providerSelected,
    selectedEntityIds,
    selectedEntityType,
  }), [
    editMode,
    hasReconciliationItems,
    // @ts-expect-error TS2339
    orderReconciliationItems.length,
    selectedEntityIds.length,
    selectedEntityType,
  ])

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrdersCount,
    loading,
  } = orderPayload

  const useReportingPayload = useReporting({
    allowedReports: ['order_reconciliation_by_provider'],
    defaultValues: {
      agency_id: agencyId, provider_id: providerId, start_date: startDate, end_date: endDate,
    },
    // @ts-expect-error TS2322
    formDeps: [providerId],
  })

  const {
    callbacks: { generateReport: generateFn },
  } = useReportingPayload

  const generateReportPayload = {
    callbacks: {
      action: () => generateFn(),
    },
    toastText: 'Report Queued',
  }

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={(
        <Link
          textProps={{ fontSize: 'small' }}
          onClick={() => {
            deselectAll()
            setState({ editMode: !editMode })
          }}
        >
          {editMode ? 'Cancel' : 'Manage Items'}
        </Link>
      )}
      title="Orders"
    >
      {!editMode && (
        <Box borderBottom="1px solid" borderColor="lineColor" paddingTop="large" paddingX="large">
          <NoticeBox variant="whiteGrey">
            <NoticeBox.Paragraph boxProps={{ marginTop: 0 }}>
              Selecting multiple orders will let you reconcile them together with the same invoice.
            </NoticeBox.Paragraph>
          </NoticeBox>
        </Box>
      )}

      <Table
        boxProps={{ border: 'none' }}
        // @ts-expect-error TS2322
        columns={memoColumns}
        // @ts-expect-error TS2322
        data={items}
        scroll={{ x: 1600 }}
        stickyHeader
      />

      {providerSelected && (
        <DashboardModule.LoadMoreFooter
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredOrdersCount}
          loading={loading}
          showBorderTop={false}
        >
          {hasSelectedEntities && selectedEntityType && (
            <Button
              buttonStyle="primaryEdit"
              icon={<FontAwesomeIcon icon={faCircleCheck} />}
              onClick={() => {
                bulkReconcile({
                  entityType: selectedEntityType,
                  selectedEntityIds,
                })
              }}
              size="medium"
              width={['100%', 'fit-content']}
              marginRight="small"
            >
              Reconcile
            </Button>
          )}

          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faDownload} />}
            onClick={() => handleCallbackAction(generateReportPayload)}
            size="medium"
            width={['100%', 'fit-content']}
          >
            Download
          </Button>
        </DashboardModule.LoadMoreFooter>
      )}

      {!providerSelected && (
        <BalanceCalculations
          campaign={campaign}
          selectedEntityType={selectedEntityType}
          isReconcilable={hasSelectedEntities}
          orderReconciliationPayload={orderReconciliationPayload}
        />
      )}
    </DashboardModule>
  )
}

export default OrderReconciliationTable
